import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  fetchProductListingGroups,
  fetchProductListings,
  type ListingFilters,
  type ProductListingGroupSort,
} from "@/lib/product-listing-api";
import { SSE_FALLBACK_POLL_MS, SSE_SAFETY_POLL_MS } from "@/lib/sse-invalidation";
import { isExpandableGroup, type ProductInventoryGate } from "./product-listing-helpers";

export type ProductStatusFilter = "advertised" | "unadvertised" | null;

/** Availability filters whose rows all have known positive stock (the stock gate's pool). */
const STOCKED_AVAILABILITIES = new Set(["IN_STOCK", "IN_STOCK_SCARCE"]);

interface UseProductListingBrowseOptions {
  shopId: string;
  initialAvailability: string | null;
  listingAdProduct: "sp" | undefined;
  unifiedTable: boolean;
  inventory: ProductInventoryGate;
}

/** Browse filters, paging, sorting and the group/listing reads they drive. */
export function useProductListingBrowse({
  shopId,
  initialAvailability,
  listingAdProduct,
  unifiedTable,
  inventory,
}: UseProductListingBrowseOptions) {
  const { t } = useTranslation("campaignCreation");
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [availability, setAvailability] = useState<string | null>(initialAvailability);
  const [productStatus, setProductStatus] = useState<ProductStatusFilter>(null);
  const [groupPage, setGroupPage] = useState(1);
  const [groupSort, setGroupSort] = useState<ProductListingGroupSort | null>(null);
  const [listingPage, setListingPage] = useState(1);
  const [activeGroup, setActiveGroup] = useState<string | null>(null);
  const initializedGroupContext = useRef<string | null>(null);
  // Set by the Shop sync stream subscriptions; a broken stream falls back to faster polling.
  const [refreshStreamFailed, setRefreshStreamFailed] = useState(false);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => window.clearTimeout(timer);
  }, [search]);

  const filters: ListingFilters = useMemo(
    () => ({
      search: debouncedSearch || undefined,
      active: "true",
      availability: availability ?? undefined,
      productStatus: productStatus ?? undefined,
      ...(listingAdProduct ? { adProduct: listingAdProduct } : {}),
    }),
    [availability, debouncedSearch, listingAdProduct, productStatus],
  );
  const filterSnapshot = useMemo(
    () => ({
      search: filters.search,
      active: filters.active ?? "true",
      // Header state and bulk selection must count the same selectable inventory pool.
      // Browse filters still keep unavailable products visible in the table.
      availability: filters.availability ?? "IN_STOCK",
      productStatus: filters.productStatus,
      ...(listingAdProduct ? { adProduct: listingAdProduct } : {}),
    }),
    [filters, listingAdProduct],
  );

  const groupQueryKey = useMemo(
    () => ["product-listings", "groups", shopId, filters, groupPage, groupSort] as const,
    [filters, groupPage, groupSort, shopId],
  );
  const listingQueryKey = useMemo(
    () => ["product-listings", "list", shopId, activeGroup, filters, listingPage] as const,
    [activeGroup, filters, listingPage, shopId],
  );
  const groups = useQuery({
    queryKey: groupQueryKey,
    queryFn: () =>
      groupSort
        ? fetchProductListingGroups(shopId, filters, groupPage, groupSort)
        : fetchProductListingGroups(shopId, filters, groupPage),
    // Sorting may retain the current rows. A different Shop, filter or page must
    // load its own rows so stale products cannot be selected in the new scope.
    placeholderData: (previousData, previousQuery) => {
      if (!previousQuery) return undefined;
      const previousKey = previousQuery.queryKey;
      return previousKey[2] === shopId &&
        JSON.stringify(previousKey[3]) === JSON.stringify(filters) &&
        previousKey[4] === groupPage
        ? previousData
        : undefined;
    },
    refetchInterval: refreshStreamFailed ? SSE_FALLBACK_POLL_MS : SSE_SAFETY_POLL_MS,
    refetchIntervalInBackground: false,
  });
  const listings = useQuery({
    queryKey: listingQueryKey,
    queryFn: () => fetchProductListings(shopId, activeGroup as string, filters, listingPage),
    enabled: Boolean(activeGroup),
    placeholderData: keepPreviousData,
    refetchInterval: refreshStreamFailed ? SSE_FALLBACK_POLL_MS : SSE_SAFETY_POLL_MS,
    refetchIntervalInBackground: false,
  });
  const inventoryFailureNotice = useRef<string | null>(null);

  useEffect(() => {
    const inventory = groups.data?.catalog.inventory;
    if (inventory?.status !== "failed") return;
    const noticeKey = `${inventory.lastAttemptAt ?? "unknown"}:${inventory.errorCode ?? "unknown"}`;
    if (inventoryFailureNotice.current === noticeKey) return;
    inventoryFailureNotice.current = noticeKey;
    toast.error(t("products.inventoryRefreshFailed"));
  }, [groups.data?.catalog.inventory, t]);

  const groupQueryContext = JSON.stringify([shopId, filters, groupPage]);

  useEffect(() => {
    // Row reads can hydrate eligibility after the group counts were loaded.
    if (listingAdProduct === "sp" && listings.dataUpdatedAt && !listings.isPlaceholderData) {
      void queryClient.invalidateQueries({ queryKey: ["product-listings", "groups", shopId] });
    }
  }, [listingAdProduct, listings.dataUpdatedAt, listings.isPlaceholderData, queryClient, shopId]);

  useEffect(() => {
    if (groups.isPending || groups.isPlaceholderData || !groups.data) return;
    const groupRows = groups.data.data;
    if (initializedGroupContext.current !== groupQueryContext) {
      initializedGroupContext.current = groupQueryContext;
      setActiveGroup(
        groupRows.find((group) => !unifiedTable || isExpandableGroup(group))?.variationGroupKey ??
          null,
      );
      setListingPage(1);
      return;
    }
    if (
      activeGroup &&
      !groupRows.some(
        (group) =>
          group.variationGroupKey === activeGroup && (!unifiedTable || isExpandableGroup(group)),
      )
    ) {
      setActiveGroup(null);
      setListingPage(1);
    }
  }, [
    activeGroup,
    groupQueryContext,
    groups.data,
    groups.isPending,
    groups.isPlaceholderData,
    unifiedTable,
  ]);

  // Under the stock gate an out-of-stock or unknown-stock view has no addable rows, so
  // bulk selection there would add SKUs whose row checkboxes are disabled and that the
  // submit-time stock preflight rejects.
  const bulkSelectable =
    inventory === "ignore" || availability === null || STOCKED_AVAILABILITIES.has(availability);

  // The selected SKU list is already local. Keep unfiltered group checkboxes in
  // sync with it instead of clearing them during the optional intersection read.
  const hasSkuFilter = Boolean(filters.search || filters.availability || filters.productStatus);

  function changeSearch(value: string) {
    setSearch(value);
    setGroupPage(1);
    setListingPage(1);
  }
  function changeAvailability(value: string | null) {
    setAvailability(value);
    setGroupPage(1);
    setListingPage(1);
  }
  function changeProductStatus(value: ProductStatusFilter) {
    setProductStatus(value);
    setGroupPage(1);
    setListingPage(1);
  }
  function showGroup(variationGroupKey: string | null) {
    setActiveGroup(variationGroupKey);
    setListingPage(1);
  }
  function toggleGroupSort(sortBy: ProductListingGroupSort["sortBy"]) {
    setGroupPage(1);
    setGroupSort((current) => ({
      sortBy,
      sortOrder: current?.sortBy === sortBy && current.sortOrder === "asc" ? "desc" : "asc",
    }));
  }

  return {
    search,
    // Row checkboxes stay disabled until the visible rows match the typed search.
    searchSettling: search.trim() !== debouncedSearch,
    availability,
    productStatus,
    groupPage,
    groupSort,
    listingPage,
    activeGroup,
    filterSnapshot,
    listingAdProduct,
    hasSkuFilter,
    bulkSelectable,
    groups,
    listings,
    refreshStreamFailed,
    setRefreshStreamFailed,
    setGroupPage,
    setListingPage,
    changeSearch,
    changeAvailability,
    changeProductStatus,
    showGroup,
    toggleGroupSort,
  };
}

export type ProductListingBrowse = ReturnType<typeof useProductListingBrowse>;
